import { useState } from "react";
import { KeyboardAvoidingView, Platform, Pressable } from "react-native";
import { ScrollView, Text, View, YStack } from "tamagui";

import { iProfile } from "../../../features/Profile";
import { Loader } from "../../../components/design-system/Loader";
import { EditProfile as EditProfileComponent } from "../../../features/Profile/components/EditProfile/EditProfile";
import { Button } from "../../../components/design-system";
import { brandShadow, customColorTokens } from "../../../../tamagui.config";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import { Header } from "../../../components/design-system/Header";
import { useLogOut } from "../../../hooks/useLogOut";
import { CloseIcon } from "../../../components/design-system/icons";
import { ProfileNavigatorParamList } from "../../../navigators/ProfileNavigator";
import { useUpdateProfile } from "./hooks/useUpdateProfile";
import { supabase } from "../../../../lib/supabase";
import { ConfirmationModal } from "../../../components/design-system/ConfirmationModal";
import { useToast } from "../../../hooks/useToast";
import { HeaderButton } from "../../../components/design-system/Button/HeaderButton";
import * as Updates from "expo-updates";
import { useApiQuery, useApiQueryClient } from "../../../../lib/api";
import { useAtomValue } from "jotai";
import { currentUserAtom } from "../../../../atoms/global";
import type { Profile } from "../../../types/profile";

export const EditProfile = () => {
  const toast = useToast();
  const user = useAtomValue(currentUserAtom);
  const queryClient = useApiQueryClient();

  const { data, isLoading: isQueryLoading } = useApiQuery<{
    profile: Profile;
  }>(["profile", user?.id], `/api/v1/profiles/${user?.id}`, {
    enabled: !!user?.id,
  });

  const profileData = data?.profile;

  const [loading, setLoading] = useState(false);

  const [profile, onProfileChange] = useState<iProfile | null>(null);
  const [logOut] = useLogOut();

  const navigation = useNavigation<NavigationProp<ProfileNavigatorParamList>>();

  const [updateProfile, isProfileUpdating] = useUpdateProfile(
    user?.id ?? "",
    () => {
      queryClient.invalidateQueries({ queryKey: ["profile", user?.id] });
      navigation.navigate("ProfileHome");
    }
  );

  const effectiveProfile = profile ?? (profileData as iProfile | undefined) ?? null;

  const onSubmit = async () => {
    const {
      name,
      avatar_url,
      breakfast_food,
      emergency_contact_name,
      emergency_contact_number,
    } = effectiveProfile ?? {};

    updateProfile({
      name,
      avatar_url,
      breakfast_food,
      emergency_contact_name,
      emergency_contact_number,
    });
  };

  const defaultValues: iProfile = {
    name: profileData?.name ?? "",
    bio: profileData?.bio ?? "",
    avatar_url: profileData?.avatar_url ?? "",
    breakfast_food: profileData?.breakfast_food ?? "",
    instagram: profileData?.instagram ?? "",
    emergency_contact_name: profileData?.emergency_contact_name ?? "",
    emergency_contact_number: profileData?.emergency_contact_number ?? "",
  };

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleDeleteAccount = async () => {
    setLoading(true);
    const { error } = await supabase
      .from("profiles")
      .delete()
      .eq("id", profileData?.id);
    if (error) {
      setLoading(false);
      toast({
        title: "Unable to delete account",
        haptic: "error",
      });
    } else {
      await logOut();
      setLoading(false);
    }
  };

  if (isQueryLoading) return <Loader />;

  return (
    <>
      <Header
        hideBorder
        title={
          <HeaderButton onPress={() => navigation.navigate("ProfileHome")}>
            <CloseIcon width={16} height={16} color="black" />
          </HeaderButton>
        }
      />
      {isProfileUpdating || loading ? <Loader /> : null}
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "position" : "height"}
        enabled
        contentContainerStyle={{
          justifyContent: "center",
          alignItems: "center",
          flex: 1,
          width: "100%",
        }}
        style={{ flex: 1, width: "100%" }}
      >
        <ScrollView
          style={{
            backgroundColor: customColorTokens.brandParchment,
            flex: 1,
          }}
          w="100%"
          contentContainerStyle={{
            paddingVertical: 16,
          }}
        >
          <EditProfileComponent
            id={profileData?.id ?? ""}
            defaultValues={defaultValues}
            onChange={(val) => onProfileChange(val)}
          />
          <View px="$4" mt="$6" gap="$3">
            <Button
              onPress={onSubmit}
              {...brandShadow}
              pressStyle={{
                bg: "$brandYellow",
                shadowColor: "$colorTransparent",
                shadowOffset: { width: 0, height: 0 },
                borderWidth: 1,
                borderColor: "black",
                scale: 0.98,
              }}
            >
              SAVE
            </Button>
            <YStack
              mt="$4"
              justifyContent="center"
              alignItems="center"
              gap="$3"
            >
              <Text>More Actions</Text>

              <Pressable onPress={logOut}>
                <Text textDecorationLine="underline" fontFamily="$heading">
                  LOG OUT
                </Text>
              </Pressable>
              <Pressable onPress={() => setIsDeleteModalOpen(true)}>
                <Text textDecorationLine="underline" fontFamily="$heading">
                  DELETE ACCOUNT
                </Text>
              </Pressable>
            </YStack>

            <YStack mt="$4" justifyContent="center" alignItems="center" pb={16}>
              <Text>Version Info</Text>
              <Text>Channel:{Updates.channel}</Text>
              <Text>Version: {Updates.runtimeVersion}</Text>
              <Text>Update ID: {Updates.updateId}</Text>
            </YStack>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
      <ConfirmationModal
        onCancel={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteAccount}
        title="Delete Account"
        subtitle="Are you sure you want to delete your account?"
        isOpen={isDeleteModalOpen}
      />
    </>
  );
};
